export type ModMatrixProps = {
  sources: string[];
  destinations: string[];
  /** Sparse map keyed as `${source}→${destination}` with amount in [-1, 1]. */
  amounts: Record<string, number>;
  selected?: { source: string; destination: string } | null;
  onSelect?: (cell: { source: string; destination: string }) => void;
  onChangeAmount?: (cell: { source: string; destination: string }, amount: number) => void;
  label?: string;
  className?: string;
};

export function modCellKey(source: string, destination: string): string {
  return `${source}\u2192${destination}`;
}

export function ModMatrix({
  sources,
  destinations,
  amounts,
  selected = null,
  onSelect,
  onChangeAmount,
  label = "Mod Matrix",
  className,
}: ModMatrixProps) {
  return (
    <div
      role="grid"
      aria-label={label}
      className={[
        "max-w-full overflow-x-auto rounded-[var(--radius-panel)] border border-hairline",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <table className="w-max max-w-none border-collapse text-[11px]">
        <thead>
          <tr>
            <th
              scope="col"
              className="sticky left-0 z-10 w-20 bg-panel px-2 py-1.5 text-left font-normal text-ink-muted"
            >
              src＼dst
            </th>
            {destinations.map((destination) => (
              <th
                key={destination}
                scope="col"
                title={destination}
                className="w-14 max-w-14 truncate bg-panel px-1 py-1.5 text-center font-normal text-ink-muted"
              >
                {destination}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sources.map((source) => (
            <tr key={source} role="row">
              <th
                scope="row"
                title={source}
                className="sticky left-0 z-10 w-20 max-w-20 truncate bg-panel px-2 py-1.5 text-left font-normal text-ink-muted"
              >
                {source}
              </th>
              {destinations.map((destination) => {
                const key = modCellKey(source, destination);
                const amount = amounts[key] ?? 0;
                const isSelected =
                  selected?.source === source && selected?.destination === destination;
                const wash = Math.min(1, Math.abs(amount));
                return (
                  <td key={key} role="gridcell" className="p-0">
                    <button
                      type="button"
                      aria-pressed={isSelected}
                      aria-label={`${source} to ${destination}, amount ${amount}`}
                      className="relative flex h-9 w-14 items-center justify-center text-ink outline-none"
                      style={{
                        backgroundColor: isSelected
                          ? `color-mix(in oklch, var(--color-signal) ${18 + wash * 40}%, var(--color-panel))`
                          : wash > 0
                            ? `color-mix(in oklch, var(--color-signal-dim) ${wash * 35}%, var(--color-panel))`
                            : "var(--color-panel)",
                      }}
                      onClick={() => onSelect?.({ source, destination })}
                      onDoubleClick={() => onChangeAmount?.({ source, destination }, 0)}
                    >
                      <span className="readout relative z-10">
                        {amount === 0 ? "·" : amount.toFixed(2)}
                      </span>
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
